import { RouteProp, useRoute } from "@react-navigation/native";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import LTILayout from "../layouts/LTILayout";
import type { LTIErrorCode, RootStackParamList } from "../navigation/navigation.types";
import LTIErrorState from "./LTIErrorState";
import LTINotConfiguredState from "./LTINotConfiguredState";

type LTIErrorViewProps = {
  error?: string;
  message?: string;
};

type MappedLTIErrorCode = Exclude<LTIErrorCode, "lti_not_configured">;

const ERROR_MESSAGE_KEYS: Record<MappedLTIErrorCode, string> = {
  invalid_launch: "This activity could not be loaded. Please try again from your LMS.",
  missing_activity: "This activity no longer exists or has not been configured.",
  auth_failed: "Authentication failed. Please try launching again from your LMS.",
  unknown: "We could not load this activity right now. Please try launching again from your LMS.",
};

const RECOVERY_MESSAGE_KEY = "Try launching this activity again from your LMS.";

function normalizeErrorCode(code?: string): LTIErrorCode {
  if (
    code === "invalid_launch" ||
    code === "missing_activity" ||
    code === "auth_failed" ||
    code === "lti_not_configured"
  ) {
    return code;
  }
  return "unknown";
}

function readQueryParam(name: string): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const value = new URLSearchParams(window.location.search).get(name);
  return value ?? undefined;
}

function LTIErrorView(props: LTIErrorViewProps) {
  const { t } = useTranslation();
  const route = useRoute<RouteProp<RootStackParamList, "LTIErrorView">>();

  const routeError = route.params?.error;
  const routeMessage = route.params?.message;

  const errorCode = useMemo(
    () => normalizeErrorCode(props.error ?? routeError ?? readQueryParam("error")),
    [props.error, routeError]
  );

  const errorMessage = useMemo(() => {
    const contextualMessage = props.message ?? routeMessage ?? readQueryParam("message");
    if (contextualMessage && contextualMessage.trim().length > 0) {
      return contextualMessage;
    }
    return t(ERROR_MESSAGE_KEYS[errorCode]);
  }, [errorCode, props.message, routeMessage, t]);

  if (errorCode === "lti_not_configured") {
    return (
      <LTILayout>
        <LTINotConfiguredState />
      </LTILayout>
    );
  }

  return (
    <LTILayout>
      <LTIErrorState message={errorMessage} recoveryMessage={t(RECOVERY_MESSAGE_KEY)} />
    </LTILayout>
  );
}

export default LTIErrorView;
